/**
 * 차트 껍데기 — 범례 · 눈금 설명 · 표 뷰 · 3상태를 한 곳에서 처리한다.
 *
 * **모든 차트는 표 뷰 쌍둥이를 갖는다.** 툴팁만으로 값을 읽게 하면 색을 못 보는
 * 사람과 키보드 사용자가 배제된다. 차트는 값을 *빠르게* 보여주는 수단이고,
 * 표는 값을 *정확하게* 읽는 수단이다 — 둘 다 있어야 한다.
 *
 * **축이 무엇인지 접어 두지 않는다** (§8.6.5). 그래프를 처음 보는 사람이 가장 먼저
 * 묻는 것이 "가로축이 뭐지?" 인데, 그 답이 각주 안에 접혀 있으면 물어볼 곳이 없다.
 * 그래서 인코딩 요약은 차트 바로 위에 **항상 펼쳐진 한 줄**로 두고, 왜 그렇게
 * 그렸는지는 읽는 법에서 자세히 설명한다.
 *
 * 이 컴포넌트가 페이지 전체의 유일한 카드 껍데기다 — 껍데기가 두 종류면 같은 행의
 * 카드 높이·플롯 시작 위치가 어긋나고, 한쪽에만 표 뷰가 없는 일이 생긴다.
 */

import { useState, type ReactNode } from 'react'

import { trustClass } from '@/lib/trust'
import type { Meta } from '@/lib/types'
import { CoverageBar } from './CoverageBar'
import './ChartFrame.css'

export interface LegendItem {
  label: string
  color: string
  /**
   * 색 단독 구분을 피하기 위한 보조 표시.
   * **범례 견본은 차트의 마크와 같은 모양이어야 한다** — 차트가 빈 원인데
   * 범례가 채워진 사각형이면 둘을 연결할 수 없다.
   */
  pattern?: 'solid' | 'hatch' | 'ring' | 'dot' | 'line'
}

/** 무엇이 무엇을 뜻하는가. `role` 은 화면에 그대로 찍히는 라벨이다. */
export interface Encoding {
  /** '가로축' · '세로축' · '색' · '점 크기' · '세로선' 등 */
  role: string
  /** 그 축이 나타내는 것. 짧게 (한 줄 요약에 들어간다) */
  what: string
  /** 읽는 법에만 나오는 자세한 설명 — 왜 그렇게 그렸는지 */
  detail?: string
}

/** 주의할 점. 라벨을 주면 읽는 법에서 굵게 앞세운다. */
export type Note = string | { label: string; text: string }

interface Props {
  title: string
  question: string
  meta?: Meta
  hideCoverage?: boolean
  headline?: ReactNode
  legend?: LegendItem[]
  /** 축·색·크기가 무엇인지. 차트 위에 한 줄로 항상 보인다 (§8.6.5) */
  encoding?: Encoding[]
  /**
   * 이 카드의 데이터를 바꾸는 조작(정렬 기준·지표 선택 등).
   *
   * **본문(children) 안에 두면 안 된다.** 표 뷰로 바꾸면 사라지는데, 정렬·지표는
   * 표에도 그대로 적용되므로 "적용은 되는데 컨트롤이 없는" 상태가 된다.
   * 그래서 뷰와 무관하게 항상 보이는 자리를 따로 둔다.
   */
  controls?: ReactNode
  /** "무엇을 보는가" 한 문장 — 읽는 법의 첫 항목 */
  reading?: string
  isLoading?: boolean
  error?: Error | null
  isEmpty?: boolean
  /** 빈 상태 문구. 기본값은 필터를 전제하므로, 필터를 타지 않는
   *  화면(예: 설치 장소)은 자기 사유를 줘야 한다 — 안 그러면 화면이
   *  "필터를 넓혀 보세요"라고 거짓말한다 */
  emptyText?: string
  onRetry?: () => void
  /**
   * 표 뷰 — 차트와 같은 값을 정확히 읽는 경로.
   *
   * **차트를 그리는 위젯은 반드시 준다.** 없어도 되는 것은 본문 자체가 이미 표이거나
   * 값을 글자로 다 보여주는 위젯(모집단 폭포·품질 매트릭스·수집 캘린더)뿐이다.
   * 이때는 전환 버튼을 띄우지 않는다 — 눌러도 같은 것이 나오면 버튼이 거짓말이다.
   */
  table?: ReactNode
  notes?: Note[]
  children: ReactNode
}

function noteKey(n: Note): string {
  return typeof n === 'string' ? n : `${n.label}:${n.text}`
}

export function ChartFrame({
  title,
  question,
  meta,
  hideCoverage = false,
  headline,
  legend = [],
  encoding = [],
  controls,
  reading,
  isLoading = false,
  error = null,
  isEmpty = false,
  emptyText,
  onRetry,
  table,
  notes = [],
  children,
}: Props) {
  const [view, setView] = useState<'chart' | 'table'>('chart')
  const hasTable = table != null
  const trust = meta ? trustClass(meta.coverage) : 'trust-high'
  const kept = notes.filter((n) => (typeof n === 'string' ? n.length > 0 : n.text.length > 0))
  const hasReading = Boolean(reading) || encoding.length > 0 || kept.length > 0

  return (
    <section className={`cframe ${trust}`} aria-busy={isLoading}>
      <header className="cframe__head">
        <div className="cframe__heading">
          <h3 className="cframe__title">{title}</h3>
          <p className="cframe__question">{question}</p>
        </div>
        <div className="cframe__aside">
          {headline}
          {meta && !hideCoverage && <CoverageBar meta={meta} compact />}
        </div>
      </header>

      {(legend.length > 0 || hasTable) && (
      <div className="cframe__bar">
        {legend.length > 0 && (
          <ul className="cframe__legend">
            {legend.map((l) => (
              <li key={l.label}>
                <span
                  className={`cframe__swatch cframe__swatch--${l.pattern ?? 'solid'}${
                    l.pattern === 'hatch' ? ' hatch-na' : ''
                  }`}
                  style={
                    l.pattern === 'ring'
                      ? { borderColor: l.color }
                      : { background: l.color, borderColor: l.color }
                  }
                  aria-hidden="true"
                />
                {l.label}
              </li>
            ))}
          </ul>
        )}
        {hasTable && (
          <div className="cframe__views" role="group" aria-label="보기 전환">
            {(['chart', 'table'] as const).map((v) => (
              <button
                key={v}
                type="button"
                aria-pressed={view === v}
                className={`cframe__view${view === v ? ' is-on' : ''}`}
                onClick={() => setView(v)}
              >
                {v === 'chart' ? '차트' : '표'}
              </button>
            ))}
          </div>
        )}
      </div>
      )}

      {controls && <div className="cframe__controls">{controls}</div>}

      {/* 눈금 요약 — 접지 않는다. "가로축이 뭐지?"의 답은 항상 보여야 한다 */}
      {encoding.length > 0 && (!hasTable || view === 'chart') && (
        <p className="cframe__axes">
          {encoding.map((e, i) => (
            <span key={e.role} className="cframe__axis">
              {i > 0 && <span className="cframe__axisdot" aria-hidden="true" />}
              <span className="cframe__axisrole">{e.role}</span>
              <span className="cframe__axiswhat">{e.what}</span>
            </span>
          ))}
        </p>
      )}

      <div className="cframe__body">
        {isLoading && (
          <p className="cframe__state" role="status">
            불러오는 중…
          </p>
        )}
        {!isLoading && error && (
          <div className="cframe__state cframe__state--error" role="alert">
            <p>데이터를 불러오지 못했습니다. 서버 상태를 확인하거나 다시 시도하세요.</p>
            <p className="cframe__detail">{error.message}</p>
            {onRetry && (
              <button type="button" className="btn-utility" onClick={onRetry}>
                다시 시도
              </button>
            )}
          </div>
        )}
        {!isLoading && !error && isEmpty && (
          <p className="cframe__state">
            {emptyText ?? '조건에 맞는 데이터가 없습니다. 필터를 넓혀 보세요.'}
          </p>
        )}
        {!isLoading && !error && !isEmpty && (!hasTable || view === 'chart' ? children : table)}
      </div>

      {hasReading && (
        <details className="cframe__foot">
          <summary>
            <span className="cframe__footlabel">읽는 법</span>
            <span className="cframe__foothint">이 그래프를 어떻게 읽나</span>
          </summary>

          <div className="cframe__reading">
            {reading && (
              <section className="cframe__part">
                <h4 className="cframe__partname">무엇을 보는가</h4>
                <p className="cframe__lead">{reading}</p>
              </section>
            )}

            {encoding.length > 0 && (
              <section className="cframe__part">
                <h4 className="cframe__partname">눈금 읽기</h4>
                <dl className="cframe__enc">
                  {encoding.map((e) => (
                    <div className="cframe__encrow" key={e.role}>
                      <dt>{e.role}</dt>
                      <dd>
                        <b>{e.what}</b>
                        {e.detail && <span className="cframe__encwhy">{e.detail}</span>}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>
            )}

            {kept.length > 0 && (
              <section className="cframe__part">
                <h4 className="cframe__partname">주의할 점</h4>
                <ul className="cframe__notes">
                  {kept.map((n) => (
                    <li key={noteKey(n)}>
                      {typeof n === 'string' ? (
                        n
                      ) : (
                        <>
                          <b>{n.label}</b> {n.text}
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        </details>
      )}
    </section>
  )
}
